/**
 * Đăng nhập.
 *
 * Deliberately spartan: three fields, one big button at the bottom. Employees
 * log in once and then stay logged in for weeks, so this screen optimises for
 * "typing a password on a phone with wet hands" rather than for looking clever.
 */
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from './AuthProvider';
import { Button } from '../components/ui';
import { ApiError } from '../lib/api';

const schema = z.object({
  tenantCode: z.string().min(1, 'Nhập mã cửa hàng.').max(64),
  username: z.string().min(1, 'Nhập tên đăng nhập.').max(120),
  password: z.string().min(1, 'Nhập mật khẩu.').max(256),
});

type FormValues = z.infer<typeof schema>;

export function LoginScreen(): JSX.Element {
  const { signIn } = useAuth();
  const [serverError, setServerError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      tenantCode: import.meta.env.VITE_DEFAULT_TENANT_CODE ?? '',
      username: '',
      password: '',
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      await signIn(values);
    } catch (error) {
      setServerError(
        error instanceof ApiError ? error.message : 'Không đăng nhập được. Vui lòng thử lại.',
      );
    }
  });

  return (
    <div className="flex min-h-full flex-col bg-brand-700">
      <div className="flex flex-1 flex-col justify-end px-6 pb-4 pt-[calc(env(safe-area-inset-top,0px)+3rem)]">
        <h1 className="text-3xl font-black text-white">SonAuto Staff</h1>
        <p className="mt-1 text-brand-100">Chấm công · Nhận việc · KPI · Lương</p>
      </div>

      <form
        onSubmit={(event) => void onSubmit(event)}
        className="rounded-t-3xl bg-white px-6 pb-[calc(env(safe-area-inset-bottom,0px)+1.5rem)] pt-6"
      >
        <label className="label" htmlFor="tenantCode">
          Mã cửa hàng
        </label>
        <input
          id="tenantCode"
          className="field mt-1"
          autoCapitalize="characters"
          autoComplete="organization"
          inputMode="text"
          {...register('tenantCode')}
        />
        {errors.tenantCode ? <p className="field-error">{errors.tenantCode.message}</p> : null}

        <label className="label mt-4" htmlFor="username">
          Tên đăng nhập
        </label>
        <input
          id="username"
          className="field mt-1"
          autoCapitalize="none"
          autoComplete="username"
          {...register('username')}
        />
        {errors.username ? <p className="field-error">{errors.username.message}</p> : null}

        <label className="label mt-4" htmlFor="password">
          Mật khẩu
        </label>
        <div className="mt-1 flex gap-2">
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            className="field"
            autoComplete="current-password"
            {...register('password')}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="tap shrink-0 rounded-xl border border-slate-300 px-3 text-sm font-semibold text-slate-700 active:bg-slate-100"
          >
            {showPassword ? 'Ẩn' : 'Hiện'}
          </button>
        </div>
        {errors.password ? <p className="field-error">{errors.password.message}</p> : null}

        {serverError === null ? null : (
          <p role="alert" className="mt-4 rounded-xl bg-rose-50 p-3 text-sm font-medium text-rose-700">
            {serverError}
          </p>
        )}

        {/* Primary action, bottom of the screen, full width, 64px tall. */}
        <Button type="submit" size="lg" fullWidth className="mt-6" loading={isSubmitting}>
          Đăng nhập
        </Button>

        <p className="mt-4 text-center text-xs text-slate-500">
          Quên mật khẩu? Liên hệ quản lý chi nhánh để được cấp lại.
        </p>
      </form>
    </div>
  );
}
